<template>
  <Menu class="w-[280px]">
    <template v-if="canRegister">
      <Menu.Item
        :icon="GlobeAddIcon"
        :disabled="anyInactive"
        @click="handleRegisterCountriesClick"
      >
        Register in more countries
        <template #description>
          Recognized and registered under local laws
        </template>
      </Menu.Item>

      <Menu.Divider />
    </template>

    <Menu.Item :icon="MoveIcon" @click="handleMoveClick"> Move </Menu.Item>
    <Menu.Item
      :icon="LockIcon"
      :disabled="!canChangePrivacy"
      @click="handlePrivacyControlsClick"
    >
      {{ privacyControlsCopy }}
    </Menu.Item>
    <Menu.Item
      class="text-red-600!"
      :icon="ArchivedIcon"
      :disabled="!canArchive"
      @click="handleArchiveClick"
    >
      Archive
    </Menu.Item>
  </Menu>
</template>

<script setup lang="ts">
import { openModal, Modals } from '@/shared/dialogs/modals'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { useBillingStore } from '@/features/billing/store'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import {
  CopyrightStatus,
  FilePermission,
  FileRole,
  type File,
} from '@/shared/graphql/router/graphql'
import { Menu } from '@dacr/library'
import { MoveIcon, LockIcon, ArchivedIcon, GlobeAddIcon } from '@dacr/icons'
import { computed } from 'vue'

const props = defineProps<{
  files: File[]
}>()
const emit = defineEmits<{
  clicked: []
}>()

const originFlagEnabled = useFeatureFlag(FeatureFlag.Origin)

const billingStore = useBillingStore()

const canRegister = computed(
  () =>
    originFlagEnabled.value &&
    billingStore.isPro &&
    props.files.every((file) => file.role === FileRole.Owner)
)

const anyInactive = computed(() =>
  props.files.some(
    (file) => file.copyright?.status === CopyrightStatus.Inactive
  )
)

const canChangePrivacy = computed(() =>
  props.files.every((file) => file.permissions.includes(FilePermission.Privacy))
)

const canArchive = computed(() =>
  props.files.every((file) => file.permissions.includes(FilePermission.Archive))
)

const privacyControlsCopy = computed(() => {
  const allFilesPublic = props.files.every((file) => !file.settings?.isPrivate)
  const allFilesPrivate = props.files.every((file) => file.settings?.isPrivate)

  if (allFilesPublic) return 'Make files private'
  if (allFilesPrivate) return 'Make files public'
  return 'Privacy controls'
})

const handleRegisterCountriesClick = () => {
  document.body.click()

  openDialog(Dialog.RegisterCountries, { props: { files: props.files } })
  emit('clicked')
}

const handleMoveClick = () => {
  document.body.click()

  openDialog(Dialog.MoveItems, {
    props: { ids: props.files.map((file) => file.id) },
  })
  emit('clicked')
}

const handlePrivacyControlsClick = () => {
  const allFilesPublic = props.files.every((file) => !file.settings?.isPrivate)
  const allFilesPrivate = props.files.every((file) => file.settings?.isPrivate)
  const ids = props.files.map((file) => file.id)

  document.body.click()

  if (allFilesPublic) {
    openModal(Modals.MakeFilesPrivate, { props: { ids } })
  } else if (allFilesPrivate) {
    openModal(Modals.MakeFilesPublic, { props: { ids } })
  } else {
    openDialog(Dialog.UpdateFilePrivacy, { props: { ids } })
  }
  emit('clicked')
}

const handleArchiveClick = () => {
  document.body.click()

  openModal(Modals.ArchiveFiles, { props: { files: props.files } })
  emit('clicked')
}
</script>
